import { ALL_INTEGRATION_TYPES, IIntegrationInput, IntegrationType } from "lib";
import {
    Form,
    Button,
    InputControl,
    TextInput,
    Picker,
    CheckBox,
    RequiredText,
    Loading,
    useThemedStyle,
    StyleFunction,
    Spacer,
} from "@venuepos/react-common";
import { useIntegrationsQuery } from "graphql-sdk";
import React, { View } from "react-native";
import { useTranslation } from "locales";

export function GeneralForm(props: {
    form: Form<IIntegrationInput>;
    onSubmit: () => void;
    submitButton: [string, string];
    pickerDisabled: boolean;
}) {
    const [t] = useTranslation();
    const styles = useThemedStyle(styleFunc);
    const [{ values, errors }, { setValue, handleSubmit }] = props.form;

    const { data, loading } = useIntegrationsQuery({
        fetchPolicy: "no-cache",
    });

    if (!values) {
        return <Loading />;
    }

    // This is all the created types. There should only be one of each.
    // (Apparently that's a limit we set for our users at some point...)
    const createdTypes: IntegrationType[] = data?.integrations.data
        ? data?.integrations.data.map(itr => itr.type as IntegrationType)
        : [];

    // The set of created types - this should at most limit to the number of available types.
    const uniqueCreatedTypes = Array.from(new Set(createdTypes));

    if (loading) {
        return <Loading />;
    }

    return (
        <View style={styles.container}>
            <InputControl error={errors.name}>
                <TextInput
                    label={t("common.name", "Name")}
                    placeholder={t(
                        "backoffice.common.enter_name",
                        "Enter name"
                    )}
                    value={values.name}
                    onChangeText={text => setValue("name", text)}
                    required={true}
                />
            </InputControl>

            <InputControl error={errors.type}>
                <Picker
                    label={t("backoffice.common.type", "Type")}
                    selectedValue={values.type}
                    onValueChange={value => {
                        setValue("type", value as IntegrationType);
                    }}
                    disabled={
                        props.pickerDisabled ||
                        uniqueCreatedTypes.length >=
                            ALL_INTEGRATION_TYPES.length
                    }
                    required={true}
                >
                    <Picker.Item
                        label={t(
                            "backoffice.integrations.choose_type",
                            "Choose type"
                        )}
                        value={undefined}
                        key="__select__"
                    />
                    {!uniqueCreatedTypes?.includes("E-CONOMIC") ||
                    values.type === "E-CONOMIC" ? (
                        <Picker.Item
                            label={t(
                                "backoffice.integrations.types.economic",
                                "E-conomic"
                            )}
                            value="E-CONOMIC"
                            key="E-CONOMIC"
                        />
                    ) : null}
                    {!uniqueCreatedTypes?.includes("PARTNER-API") ||
                    values.type === "PARTNER-API" ? (
                        <Picker.Item
                            label={t(
                                "backoffice.integrations.types.partner-api",
                                "PartnerAPI"
                            )}
                            value="PARTNER-API"
                            key="PARTNER-API"
                        />
                    ) : null}
                    {!uniqueCreatedTypes?.includes("VENUE-ACCESS") ||
                    values.type === "VENUE-ACCESS" ? (
                        <Picker.Item
                            label={t(
                                "backoffice.integrations.types.venue-access",
                                "Venue Access"
                            )}
                            value="VENUE-ACCESS"
                            key="VENUE-ACCESS"
                        />
                    ) : null}
                    {!uniqueCreatedTypes?.includes("CMP-PRODUCT") ||
                    values.type === "CMP-PRODUCT" ? (
                        <Picker.Item
                            label={t(
                                "backoffice.integrations.types.cmp-product",
                                "CMP"
                            )}
                            value="CMP-PRODUCT"
                            key="CMP-PRODUCT"
                        />
                    ) : null}
                    {!uniqueCreatedTypes?.includes("FISKALY") ||
                    values.type === "FISKALY" ? (
                        <Picker.Item
                            label={t(
                                "backoffice.integrations.types.fiskaly",
                                "Fiskaly"
                            )}
                            value="FISKALY"
                            key="FISKALY"
                        />
                    ) : null}
                    {!uniqueCreatedTypes?.includes("VENUE-INVENTORY") ||
                    values.type === "VENUE-INVENTORY" ? (
                        <Picker.Item
                            label={t(
                                "backoffice.integrations.types.venue-inventory",
                                "Venue Inventory"
                            )}
                            value="VENUE-INVENTORY"
                        />
                    ) : null}
                </Picker>
            </InputControl>

            <InputControl error={errors.active}>
                <CheckBox
                    label={t("backoffice.common.active", "Active")}
                    value={values.active}
                    onValueChange={value => setValue("active", value)}
                    disabled={values.type === "PARTNER-API"}
                />
            </InputControl>

            <InputControl
                error={errors.apiKey}
                description={t(
                    "backoffice.integrations.api_key_description",
                    "The API key is needed for communication with the integrated system.\n\nIf this is an internal API (i.e. the VM POS Partner API), you will need to set this value yourself. If it is for an external API, then that system will have to supply a value for this.\n\nIf this is a new Fiskaly integration, then this value will be overwritten, when the integration is saved the first time. You can put any string here."
                )}
            >
                <TextInput
                    label={t("backoffice.integrations.api_key", "API key")}
                    placeholder={t(
                        "backoffice.integrations.enter_api_key",
                        "Enter API key"
                    )}
                    value={values.apiKey}
                    onChangeText={text => setValue("apiKey", text)}
                    required={true}
                />
            </InputControl>

            <Spacer space={2} />

            <Button onPress={handleSubmit(props.onSubmit)}>
                {t(props.submitButton[0], props.submitButton[1])}
            </Button>

            <RequiredText />
        </View>
    );
}

const styleFunc: StyleFunction = theme => ({
    container: {
        maxWidth: theme.dimensions.maxFormWidth,
    },
});
